import { httpClient } from './http-client';
import { API_ENDPOINTS } from '../config/api';
import { readSession, writeSession, clearSession } from './session-storage';
import type { UserRole } from '../store/auth.store';

export interface UserInfo {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  userInfo: UserInfo;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

class AuthService {
  private readonly endpoints = API_ENDPOINTS.AUTH;

  async login(email: string, password: string): Promise<ApiResponse<LoginResponse>> {
    return httpClient.post<LoginResponse>(this.endpoints.LOGIN, { email, password });
  }

  async sendVerificationCode(email: string): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.SEND_VERIFICATION_CODE, { email });
  }

  async verifyCodeAndRegister(data: {
    email: string;
    verificationCode: string;
    firstName: string;
    lastName: string;
    password: string;
  }): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.VERIFY_CODE_AND_REGISTER, data);
  }

  async forgotPassword(email: string): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.FORGOT_PASSWORD, { email });
  }

  async resetPassword(data: {
    email: string;
    verificationCode: string;
    newPassword: string;
  }): Promise<ApiResponse<unknown>> {
    return httpClient.post(this.endpoints.RESET_PASSWORD, data);
  }

  saveTokens(token: string, refreshToken: string, userInfo: UserInfo): void {
    writeSession({ token, refreshToken, userInfo });
  }

  getStoredToken(): string | null {
    return readSession()?.token ?? null;
  }

  getStoredRefreshToken(): string | null {
    return readSession()?.refreshToken ?? null;
  }

  getStoredUserInfo(): UserInfo | null {
    return readSession()?.userInfo ?? null;
  }

  clearTokens(): void {
    clearSession();
  }
}

export const authService = new AuthService();
